import { Injectable } from '@angular/core';

import { Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { setInitialStoreState } from '../../../../../../common/utils';
import { CompartmentRuleItem } from '../../../../administration/compartment/models';
import { HubContextFacade } from '../../../../administration/hub/context/hub-context.facade';
import { HubBrief } from '../../../../administration/hub/models';
import { SlotCompactInfo } from '../../models/allocation-view-calendar/allocation-calendar-slot.model';
import { AllocationViewRuleBriefFacade } from '../../rule/allocation-view-rule-brief.facade';
import { UliRule } from '../models';
import { UliCreationFacade } from './uli-creation.facade';
import { UliCreationService } from './uli-creation.service';
import { AllocationViewCalendarFacade } from '../../calendar';
import { AllocationCalendarDockModel } from '../../models';

export type RuleUliGuardData = [SlotCompactInfo, CompartmentRuleItem, AllocationCalendarDockModel[]];

@Injectable({
  providedIn: 'root',
})
export class RuleUliCreationGuard {
  constructor(
    private allocationViewRuleBriefFacade: AllocationViewRuleBriefFacade,
    private uliCreationFacade: UliCreationFacade,
    private hubContextFacade: HubContextFacade,
    private uliCreationService: UliCreationService,
    private allocationViewCalendarFacade: AllocationViewCalendarFacade,
  ) {}

  canActivate(): Observable<boolean> {
    const calendarData$ = this.allocationViewCalendarFacade.calendarData$.pipe(catchError(() => of(null)));

    return this.hubContextFacade.selectedHub$.pipe(
      filter((hub: HubBrief) => !!hub),
      take(1),
      switchMap((hub: HubBrief) =>
        combineLatest([
          this.allocationViewRuleBriefFacade.slotCompactInfo$.pipe(take(1)),
          this.allocationViewRuleBriefFacade.rule$.pipe(take(1)),
          calendarData$.pipe(take(1)),
        ]).pipe(
          tap(([slotCompactInfo, compartmentRuleItem, calendarData]: RuleUliGuardData) => {
            const storageData = this.uliCreationService.loadUliRule();

            const isUliRuleInStorage = !!storageData;
            const isSlotCompactInfo = !!slotCompactInfo;
            const isCalendarData = !!calendarData;
            const isRuleItem = !!compartmentRuleItem;
            const isUliRuleAndSlotCompactInfoInit = isSlotCompactInfo && isRuleItem;
            const isPageRefresh = !isSlotCompactInfo && !isRuleItem && !isCalendarData && isUliRuleInStorage;

            if (isUliRuleAndSlotCompactInfoInit) {
              this.uliCreationFacade.setUliRule(
                new UliRule({
                  rule: compartmentRuleItem,
                  slot: {
                    timeFrame: slotCompactInfo.timeFrame,
                    ruleId: slotCompactInfo.ruleId,
                    compartmentId: slotCompactInfo.compartmentId,
                    slotId: slotCompactInfo.slotId,
                  },
                  hubTimezone: hub.timezone,
                  dockInformation: calendarData?.find(
                    (data: AllocationCalendarDockModel) => data.dockId === compartmentRuleItem.dockId,
                  ),
                }),
              );
            }

            if (isPageRefresh) {
              this.uliCreationFacade.setUliRule(storageData);
            }
          }),
          map(([]: RuleUliGuardData) => true),
        ),
      ),
    );
  }

  canDeactivate(): boolean {
    const deactivate = [
      setInitialStoreState(this.allocationViewRuleBriefFacade),
      setInitialStoreState(this.uliCreationFacade),
    ];
    this.uliCreationService.clearStorage();

    return deactivate.every((condition: boolean) => condition === true);
  }
}
